import { useEffect, useState } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle, Copy, ArrowRight, WarningCircle } from '@phosphor-icons/react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Stripe checkout success page — polls /api/billing/checkout/status
 * until payment is confirmed, then displays the customer's auto-generated
 * email + temp password and a "Sign in" button.
 */
export default function CheckoutSuccessPage() {
  const [params] = useSearchParams();
  const sessionId = params.get('session_id');
  const navigate = useNavigate();

  const [status, setStatus] = useState('polling'); // polling | paid | failed | timeout
  const [creds, setCreds] = useState(null);        // {email, temp_password}
  const [attempts, setAttempts] = useState(0);
  const [copied, setCopied] = useState(false);
  const [affiliate, setAffiliate] = useState(null); // {first_name, name} from Rewardful

  // Pull the affiliate this purchase was attributed to (if any) so we can thank them by name.
  // Same async pattern as HomePage: Rewardful sets window.Rewardful.affiliate after its API call.
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const capture = () => {
      const a = window.Rewardful && window.Rewardful.affiliate;
      if (a && (a.first_name || a.name)) setAffiliate(a);
    };
    if (typeof window.rewardful === 'function') {
      try { window.rewardful('ready', capture); } catch {}
    }
    let polls = 0;
    const id = setInterval(() => {
      polls++;
      capture();
      if (polls >= 10) clearInterval(id);
    }, 500);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!sessionId) {
      setStatus('failed');
      return;
    }

    let cancelled = false;
    const MAX_ATTEMPTS = 30; // 30 × 2s = 1 min
    const POLL_INTERVAL_MS = 2000;

    const poll = async (attempt) => {
      if (cancelled) return;
      if (attempt >= MAX_ATTEMPTS) {
        setStatus('timeout');
        return;
      }
      setAttempts(attempt + 1);
      try {
        const { data } = await axios.get(`${API_URL}/api/billing/checkout/status/${sessionId}`);
        if (data.payment_status === 'paid') {
          setCreds({ email: data.email, temp_password: data.temp_password });
          setStatus('paid');
          return;
        }
        if (data.status === 'expired') {
          setStatus('failed');
          return;
        }
        setTimeout(() => poll(attempt + 1), POLL_INTERVAL_MS);
      } catch {
        setTimeout(() => poll(attempt + 1), POLL_INTERVAL_MS);
      }
    };
    poll(0);
    return () => { cancelled = true; };
  }, [sessionId]);

  const copyCreds = () => {
    if (!creds) return;
    const text = `Email: ${creds.email}\nPassword: ${creds.temp_password || '(check your email)'}`;
    navigator.clipboard?.writeText(text).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="min-h-screen bg-slate-50 px-6 py-12 flex items-center justify-center" data-testid="checkout-success-page">
      <div className="max-w-lg w-full">
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-8 text-center">
          {status === 'polling' && (
            <div data-testid="checkout-polling">
              <div className="w-12 h-12 mx-auto mb-4 border-4 border-[#002FA7] border-t-transparent rounded-full animate-spin" />
              <h1 className="text-2xl font-black mb-2">Confirming your payment…</h1>
              <p className="text-slate-600 text-sm">This usually takes 5&ndash;15 seconds. Please don&apos;t close this page.</p>
              <p className="text-xs text-slate-400 mt-4">Attempt {attempts}/30</p>
            </div>
          )}

          {status === 'paid' && creds && (
            <div data-testid="checkout-success">
              <CheckCircle size={56} weight="fill" className="text-green-500 mx-auto mb-4" />
              <h1 className="text-3xl font-black mb-2">Welcome aboard!</h1>
              <p className="text-slate-600 mb-2">
                <strong className="text-slate-900">Your 14-day free trial has started.</strong>
              </p>
              <p className="text-slate-500 text-sm mb-6">
                $0 charged today. Your card will be charged $30/user in 14 days unless you cancel from <b>Settings → Manage subscription</b>.
              </p>
              {affiliate && (
                <div
                  data-testid="checkout-affiliate-thanks"
                  className="bg-amber-50 border border-amber-200 rounded-md px-4 py-3 mb-5 text-left flex items-start gap-3"
                >
                  <span className="text-2xl leading-none mt-0.5" aria-hidden>🎁</span>
                  <div className="text-sm">
                    <div className="font-semibold text-slate-800">
                      Welcome via {affiliate.first_name || affiliate.name}.
                    </div>
                    <div className="text-xs text-slate-600 mt-0.5">
                      You came in through one of our affiliates &mdash; they&apos;ll be credited when your trial converts.
                    </div>
                  </div>
                </div>
              )}
              <div className="bg-slate-50 border border-slate-200 rounded-lg p-5 text-left mb-5">
                <div className="text-xs font-semibold text-slate-500 uppercase mb-1">Email</div>
                <div className="font-mono text-sm mb-3 break-all">{creds.email}</div>
                {creds.temp_password ? (
                  <>
                    <div className="text-xs font-semibold text-slate-500 uppercase mb-1">Temporary password</div>
                    <div className="font-mono text-sm bg-amber-50 border border-amber-200 rounded px-2 py-1.5 break-all" data-testid="checkout-temp-password">
                      {creds.temp_password}
                    </div>
                    <p className="text-xs text-slate-500 mt-2">Save this somewhere safe. You can change it under <b>Settings → Account</b> after you sign in.</p>
                  </>
                ) : (
                  <p className="text-xs text-slate-500">
                    Your login details have been emailed to you. Check your inbox (and spam folder).
                  </p>
                )}
              </div>
              {creds.temp_password && (
                <button
                  onClick={copyCreds}
                  data-testid="checkout-copy-creds"
                  className="text-sm text-[#002FA7] hover:text-[#001E6F] font-semibold inline-flex items-center gap-1.5 mb-4"
                >
                  <Copy size={14} weight="bold" />
                  {copied ? 'Copied!' : 'Copy email + password'}
                </button>
              )}
              <button
                onClick={() => navigate('/login')}
                data-testid="checkout-signin-cta"
                className="w-full bg-[#F39C12] hover:bg-[#E08C00] text-black font-bold py-3 rounded inline-flex items-center justify-center gap-2 transition-colors"
              >
                Sign in to SalesCrunchCRM <ArrowRight size={18} weight="bold" />
              </button>
            </div>
          )}

          {(status === 'failed' || status === 'timeout') && (
            <div data-testid="checkout-failed">
              <WarningCircle size={56} weight="fill" className="text-amber-500 mx-auto mb-4" />
              <h1 className="text-2xl font-black mb-2">
                {status === 'timeout' ? 'Still processing…' : 'Hmm, that didn\'t work.'}
              </h1>
              <p className="text-slate-600 mb-6">
                {status === 'timeout'
                  ? 'Stripe is taking longer than usual to confirm your payment. Your subscription is almost certainly active &mdash; check your email for login details, or contact us at hello@salescrunchcrm.com.'
                  : 'We couldn\'t confirm your payment. If you were charged, email us at hello@salescrunchcrm.com and we\'ll fix it immediately.'}
              </p>
              <Link
                to="/"
                className="inline-block bg-slate-100 hover:bg-slate-200 text-slate-900 font-semibold px-5 py-2 rounded text-sm"
              >
                ← Back to homepage
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
